import { Button, Card, CardContent } from '@atreus/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Building2, Plus, X } from 'lucide-react';
import { useState } from 'react';

import { apiGet, apiPost } from '../cloud.js';

interface TenantRow {
  id: string;
  slug: string;
  name: string;
  active: boolean;
  createdAt: number;
}

export const TenantsPage = () => {
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => apiGet<{ tenants: TenantRow[] }>('/admin/tenants'),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-8">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Tenants</h1>
          <p className="mt-1 text-fg-muted">
            Cuentas de cliente que usan Atreus.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" /> Crear tenant
        </Button>
      </header>

      {list.isLoading ? (
        <p className="text-fg-muted">Cargando…</p>
      ) : list.error ? (
        <p className="text-danger">Error: {(list.error as Error).message}</p>
      ) : !list.data?.tenants.length ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Building2 className="size-10 text-fg-subtle" />
            <p className="text-fg-muted">Sin tenants todavía. Crea el primero.</p>
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" /> Crear tenant
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {list.data.tenants.map((t) => (
                <li key={t.id}>
                  <Link
                    to="/tenants/$id"
                    params={{ id: t.id }}
                    className="flex items-center justify-between gap-4 p-4 hover:bg-surface-muted"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-bold truncate">{t.name}</div>
                      <div className="font-mono text-xs text-fg-muted">{t.slug}</div>
                    </div>
                    {t.active ? (
                      <span className="rounded-full border border-success/40 bg-success/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-success">
                        Activo
                      </span>
                    ) : (
                      <span className="rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
                        Inactivo
                      </span>
                    )}
                    <span className="text-xs text-fg-muted">
                      {new Date(t.createdAt).toLocaleDateString('es-CO')}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {creating ? <CreateTenantDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
};

interface CreateTenantBody {
  slug: string;
  name: string;
  preset: 'restobar' | 'juice_bar' | 'quick_service' | 'retail';
  adminEmail: string;
  adminPassword: string;
  adminFullName: string;
}

const PRESET_LABELS: Record<CreateTenantBody['preset'], string> = {
  restobar: 'Restobar (mesas + domicilio + cocina + propina)',
  juice_bar: 'Juguería (domicilio + propina)',
  quick_service: 'Comida rápida (domicilio + cocina)',
  retail: 'Retail / ferretería (sólo mostrador)',
};

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const CreateTenantDialog = ({ onClose }: { onClose: () => void }) => {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugDirty, setSlugDirty] = useState(false);
  const [preset, setPreset] = useState<CreateTenantBody['preset']>('restobar');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminFullName, setAdminFullName] = useState('Administrador');

  const create = useMutation({
    mutationFn: (body: CreateTenantBody) =>
      apiPost<{ tenant: TenantRow; admin: { email: string } }>('/admin/tenants', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      onClose();
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (create.isPending) return;
    create.mutate({
      slug: slug || slugify(name),
      name,
      preset,
      adminEmail,
      adminPassword,
      adminFullName,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-border bg-surface-raised shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-lg font-black tracking-tight">Crear tenant</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
            <X className="size-4" />
          </Button>
        </header>

        <form onSubmit={submit} autoComplete="off" className="space-y-3 p-5">
          <div>
            <label className="mb-1 block text-sm font-semibold">Nombre comercial</label>
            <input
              autoFocus
              autoComplete="off"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!slugDirty) setSlug(slugify(e.target.value));
              }}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
              placeholder="Bar La Esquina"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Slug</label>
            <input
              autoComplete="off"
              value={slug}
              onChange={(e) => {
                setSlugDirty(true);
                setSlug(slugify(e.target.value));
              }}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm"
              placeholder="bar-la-esquina"
            />
            <p className="mt-1 text-xs text-fg-muted">
              Identificador URL-friendly, único.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Preset / vertical</label>
            <select
              autoComplete="off"
              value={preset}
              onChange={(e) => setPreset(e.target.value as CreateTenantBody['preset'])}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
            >
              {(Object.keys(PRESET_LABELS) as CreateTenantBody['preset'][]).map((p) => (
                <option key={p} value={p}>
                  {PRESET_LABELS[p]}
                </option>
              ))}
            </select>
          </div>
          <hr className="border-border" />
          <div>
            <label className="mb-1 block text-sm font-semibold">Email del admin inicial</label>
            <input
              type="email"
              autoComplete="off"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
              placeholder="dueno@cliente.com"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Nombre del admin</label>
            <input
              autoComplete="off"
              value={adminFullName}
              onChange={(e) => setAdminFullName(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Contraseña inicial</label>
            <input
              type="password"
              autoComplete="new-password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
              placeholder="Mínimo 8 caracteres"
            />
          </div>

          {create.error ? (
            <p className="text-sm text-danger">{(create.error as Error).message}</p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                create.isPending ||
                !name ||
                !slug ||
                !adminEmail ||
                adminPassword.length < 8
              }
            >
              {create.isPending ? 'Creando…' : 'Crear tenant'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
